import { z } from "zod";

const ref = z.string().regex(/^e\d+_\d+$/);
const target = { ref, element: z.string().max(500).optional() };
const empty = z.object({}).strict();
const definitions = {
  browser_snapshot: ["Read the shared tab's accessibility tree and element refs.", empty],
  browser_take_screenshot: ["Take a screenshot of the shared tab's visible viewport.", empty],
  browser_navigate: [
    "Open a web URL in the shared tab.",
    z.object({ url: z.string().url().max(8000) }).strict(),
  ],
  browser_navigate_back: ["Go back in the shared tab.", empty],
  browser_click: ["Click a ref from the most recent snapshot.", z.object(target).strict()],
  browser_hover: [
    "Move the pointer to a ref from the most recent snapshot.",
    z.object(target).strict(),
  ],
  browser_type: [
    "Replace a field's text. Optionally submit it with Enter.",
    z.object({ ...target, text: z.string().max(100_000), submit: z.boolean().optional() }).strict(),
  ],
  browser_press_key: [
    "Press a key, such as Enter, Tab, Escape, or Control+A.",
    z.object({ key: z.string().max(100) }).strict(),
  ],
  browser_select_option: [
    "Choose values in a native select element.",
    z.object({ ...target, values: z.array(z.string().max(2000)).min(1).max(100) }).strict(),
  ],
  browser_scroll: [
    "Scroll the shared tab's viewport by pixels.",
    z
      .object({
        x: z.number().min(-10000).max(10000).default(0),
        y: z.number().min(-10000).max(10000),
      })
      .strict(),
  ],
  browser_tabs: [
    "List the tab the user has explicitly shared. To use another tab, ask the user to share it from the Quinn extension.",
    z.object({ action: z.literal("list").default("list") }).strict(),
  ],
} satisfies Record<string, [string, z.ZodType]>;

export const extensionTools = Object.entries(definitions).map(([name, [description, schema]]) => ({
  name,
  description,
  inputSchema: z.toJSONSchema(schema),
}));

export function parseExtensionCall(tool: string, args: unknown): Record<string, unknown> {
  if (!Object.hasOwn(definitions, tool)) throw new Error("This browser operation is not available");
  return definitions[tool as keyof typeof definitions][1].parse(args);
}

export function webUrl(value: string): string {
  const url = new URL(value);
  if (!["https:", "http:"].includes(url.protocol) || url.username || url.password)
    throw new Error("Only web pages can be shared or opened");
  return url.href;
}
